import { useEffect, useMemo, useState } from "react";
import { parseCountInput } from "../adminUtils";
import {
  createAdminInventoryBlock,
  deleteAdminInventoryBlock,
  updateAdminLocationInventory,
} from "../../../api/adminLocations";

const toDatetimeLocalInput = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fromDatetimeLocalInput = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const createDefaultBlockForm = () => {
  const start = new Date();
  start.setMinutes(Math.ceil(start.getMinutes() / 10) * 10, 0, 0);
  const end = new Date(start.getTime() + 2 * 60 * 60000);
  return {
    quantity: "1",
    startLocal: toDatetimeLocalInput(start),
    endLocal: toDatetimeLocalInput(end),
    reason: "",
  };
};

const mapInventoryBlock = (block) => ({
  id: block.id,
  locationId: block.locationId,
  equipmentTypeId: block.equipmentTypeId,
  quantity: block.quantity ?? 0,
  startUtc: block.startUtc,
  endUtc: block.endUtc,
  reason: block.reason || "",
});

const useAdminInventory = ({
  dashboard,
  locations,
  setLocations,
  showFeedback,
  refreshDashboard,
}) => {
  const [selectedEquipmentTypeId, setSelectedEquipmentTypeId] = useState(null);
  const [equipmentAdjustments, setEquipmentAdjustments] = useState({});
  const [equipmentBlockForms, setEquipmentBlockForms] = useState({});
  const [equipmentSavingId, setEquipmentSavingId] = useState(null);
  const [equipmentBlockActionId, setEquipmentBlockActionId] = useState(null);

  const equipmentSnapshot = useMemo(() => {
    const utilization = Array.isArray(dashboard?.utilizationByEquipmentType)
      ? dashboard.utilizationByEquipmentType
      : [];
    return utilization
      .map((item) => {
        const total = Number.isFinite(item.inventoryCount) ? item.inventoryCount : 0;
        const maintenance = Number.isFinite(item.maintenanceCount) ? item.maintenanceCount : 0;
        const lost = Number.isFinite(item.lostCount) ? item.lostCount : 0;
        const available = Math.max(0, total - maintenance - lost);
        return {
          id: item.equipmentTypeId,
          name: item.equipmentTypeName || "Equipment",
          total,
          available,
          maintenance,
          lost,
          utilizationRate: Number.isFinite(item.utilizationRate) ? item.utilizationRate : 0,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dashboard]);

  const selectedEquipment = useMemo(
    () => equipmentSnapshot.find((item) => item.id === selectedEquipmentTypeId) || null,
    [equipmentSnapshot, selectedEquipmentTypeId]
  );

  useEffect(() => {
    if (!selectedEquipmentTypeId) {
      setEquipmentAdjustments({});
      return;
    }
    const next = {};
    locations.forEach((location) => {
      const item = location.inventory.find(
        (entry) => entry.equipmentTypeId === selectedEquipmentTypeId
      );
      next[location.id] = {
        quantity: item?.quantity ?? 0,
        maintenance: item?.maintenanceQuantity ?? 0,
        lost: item?.lostQuantity ?? 0,
      };
    });
    setEquipmentAdjustments(next);
    setEquipmentBlockForms((prev) => {
      const nextForms = {};
      locations.forEach((location) => {
        nextForms[location.id] = prev[location.id] || createDefaultBlockForm();
      });
      return nextForms;
    });
  }, [locations, selectedEquipmentTypeId]);

  const handleEquipmentAdjustmentChange = (locationId, field) => (event) => {
    const value = event.target.value;
    setEquipmentAdjustments((prev) => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        [field]: value,
      },
    }));
  };

  const handleEquipmentAvailableChange = (locationId) => (event) => {
    const value = event.target.value;
    setEquipmentAdjustments((prev) => {
      const current = prev[locationId] || { quantity: 0, maintenance: 0, lost: 0 };
      const maintenance = parseCountInput(current.maintenance);
      const lost = parseCountInput(current.lost);
      const available = parseCountInput(value);
      const nextQuantity = Math.max(0, available + maintenance + lost);
      return {
        ...prev,
        [locationId]: {
          ...current,
          quantity: String(nextQuantity),
        },
      };
    });
  };

  const handleEquipmentSave = async (locationId) => {
    if (!selectedEquipmentTypeId) {
      showFeedback("Select an equipment type first.", "warning");
      return;
    }
    const adjustment = equipmentAdjustments[locationId];
    if (!adjustment) {
      showFeedback("No adjustments to save.", "warning");
      return;
    }
    const quantity = parseCountInput(adjustment.quantity);
    const maintenance = parseCountInput(adjustment.maintenance);
    const lost = parseCountInput(adjustment.lost);
    if (quantity < 0 || maintenance < 0 || lost < 0) {
      showFeedback("Counts cannot be negative.", "warning");
      return;
    }
    if (maintenance + lost > quantity) {
      showFeedback("Maintenance + lost cannot exceed total units.", "warning");
      return;
    }
    setEquipmentSavingId(locationId);
    try {
      const payload = await updateAdminLocationInventory(locationId, selectedEquipmentTypeId, {
        quantity,
        maintenanceQuantity: maintenance,
        lostQuantity: lost,
      });
      const updated = {
        equipmentTypeId: payload.equipmentTypeId,
        equipmentTypeName: payload.equipmentTypeName || "Equipment",
        quantity: payload.quantity ?? quantity,
        maintenanceQuantity: payload.maintenanceQuantity ?? maintenance,
        lostQuantity: payload.lostQuantity ?? lost,
        manualInUseQuantity: 0,
      };
      setLocations((prev) =>
        prev.map((location) => {
          if (location.id !== locationId) return location;
          const index = location.inventory.findIndex(
            (entry) => entry.equipmentTypeId === selectedEquipmentTypeId
          );
          const existingBlocks = index >= 0 ? location.inventory[index].blocks || [] : [];
          const nextUpdated = {
            ...updated,
            blocks: Array.isArray(payload.blocks) ? payload.blocks : existingBlocks,
          };
          const nextInventory =
            index >= 0
              ? location.inventory.map((entry) =>
                  entry.equipmentTypeId === selectedEquipmentTypeId ? nextUpdated : entry
                )
              : [...location.inventory, nextUpdated];
          return { ...location, inventory: nextInventory };
        })
      );
      setEquipmentAdjustments((prev) => ({
        ...prev,
        [locationId]: {
          quantity: updated.quantity,
          maintenance: updated.maintenanceQuantity,
          lost: updated.lostQuantity,
        },
      }));
      await refreshDashboard({ silent: true });
      showFeedback("Equipment counts updated.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to update equipment counts.", "warning");
    } finally {
      setEquipmentSavingId(null);
    }
  };

  const handleEquipmentBlockFormChange = (locationId, field) => (event) => {
    const value = event.target.value;
    setEquipmentBlockForms((prev) => ({
      ...prev,
      [locationId]: {
        ...(prev[locationId] || createDefaultBlockForm()),
        [field]: value,
      },
    }));
  };

  const handleEquipmentBlockCreate = async (locationId) => {
    if (!selectedEquipmentTypeId) {
      showFeedback("Select an equipment type first.", "warning");
      return;
    }

    const form = equipmentBlockForms[locationId] || createDefaultBlockForm();
    const quantity = parseCountInput(form.quantity);
    const startUtc = fromDatetimeLocalInput(form.startLocal);
    const endUtc = fromDatetimeLocalInput(form.endLocal);

    if (quantity <= 0) {
      showFeedback("Block quantity must be greater than zero.", "warning");
      return;
    }
    if (!startUtc || !endUtc) {
      showFeedback("Choose a valid block start and end time.", "warning");
      return;
    }
    if (new Date(endUtc) <= new Date(startUtc)) {
      showFeedback("Block end time must be after start time.", "warning");
      return;
    }

    setEquipmentBlockActionId(`${locationId}:create`);
    try {
      const payload = await createAdminInventoryBlock(locationId, selectedEquipmentTypeId, {
        quantity,
        startUtc,
        endUtc,
        reason: form.reason?.trim() || null,
      });
      const created = mapInventoryBlock(payload);
      setLocations((prev) =>
        prev.map((location) => {
          if (location.id !== locationId) return location;
          return {
            ...location,
            inventory: location.inventory.map((item) =>
              item.equipmentTypeId === selectedEquipmentTypeId
                ? { ...item, blocks: [...(item.blocks || []), created] }
                : item
            ),
          };
        })
      );
      setEquipmentBlockForms((prev) => ({
        ...prev,
        [locationId]: createDefaultBlockForm(),
      }));
      showFeedback("Equipment block added.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to add equipment block.", "warning");
    } finally {
      setEquipmentBlockActionId(null);
    }
  };

  const handleEquipmentBlockDelete = async (locationId, blockId) => {
    if (!selectedEquipmentTypeId || !blockId) return;

    setEquipmentBlockActionId(blockId);
    try {
      await deleteAdminInventoryBlock(locationId, selectedEquipmentTypeId, blockId);
      setLocations((prev) =>
        prev.map((location) => {
          if (location.id !== locationId) return location;
          return {
            ...location,
            inventory: location.inventory.map((item) =>
              item.equipmentTypeId === selectedEquipmentTypeId
                ? {
                    ...item,
                    blocks: (item.blocks || []).filter((block) => block.id !== blockId),
                  }
                : item
            ),
          };
        })
      );
      showFeedback("Equipment block removed.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to remove equipment block.", "warning");
    } finally {
      setEquipmentBlockActionId(null);
    }
  };

  const clearLocationAdjustments = (locationId) => {
    setEquipmentAdjustments((prev) => {
      if (!prev[locationId]) return prev;
      const next = { ...prev };
      delete next[locationId];
      return next;
    });
    setEquipmentBlockForms((prev) => {
      if (!prev[locationId]) return prev;
      const next = { ...prev };
      delete next[locationId];
      return next;
    });
  };

  return {
    selectedEquipmentTypeId,
    setSelectedEquipmentTypeId,
    equipmentAdjustments,
    equipmentBlockForms,
    equipmentSavingId,
    equipmentBlockActionId,
    equipmentSnapshot,
    selectedEquipment,
    handleEquipmentAdjustmentChange,
    handleEquipmentAvailableChange,
    handleEquipmentSave,
    handleEquipmentBlockFormChange,
    handleEquipmentBlockCreate,
    handleEquipmentBlockDelete,
    clearLocationAdjustments,
  };
};

export default useAdminInventory;
